import classcat from "classcat"
import capitalize from "lodash-es/capitalize"
import React from "react"
import { useFormContext } from "react-hook-form"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./BudgetEditor.module.css"

import { formatCurrency } from "~/helpers/CurrencyHelper"
import { formatMinutesToDays } from "~/helpers/budgetHelper"
import { getDefaultRate } from "~/helpers/rate-helpers"
import { formatMinuteToHours } from "~/helpers/time"

import DropdownSelector from "~/common/DropdownSelector"

import { RATE_TYPES, RateTypeSelection } from "~/GLOBALVARS"
import BudgetItemRow from "~/forms/ProjectBudgetForm/Budget/BudgetItemRow"
import OtherExpensesRow from "~/forms/ProjectBudgetForm/Budget/OtherExpensesRow"
import RoleAdder from "~/forms/ProjectBudgetForm/Budget/RoleAdder"
import TableInfo from "~/forms/ProjectBudgetForm/TableInfo"

import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"
import { BudgetedRole, RateCard } from "../types"

type Props = {
  rateCard: RateCard
  defaultFullTimeMinutes: number
  unallocatedRoles: Array<BudgetedRole>
  onAddRole: (id: number) => void
  onShowAllRoles: () => void
}

const BudgetEditor = (props: Props) => {
  const {
    rateCard,
    defaultFullTimeMinutes,
    unallocatedRoles,
    onAddRole,
    onShowAllRoles,
  } = props

  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  const {
    totalAllocated,
    hideFinancials,
    extractedRoles,
    showNonBillableView,
  } = useProjectBudgetFormContext()

  const rateType =
    RATE_TYPES.find((rt) => rt.value === formData.rateType) || RATE_TYPES[0]
  const showDayView = rateType.value === "days"

  const expenseRole = extractedRoles.expenses

  const totalMinutes = extractedRoles.people.reduce(
    (prev, curr) =>
      curr.estimatedMinutes ? prev + curr.estimatedMinutes : prev,
    0,
  )

  const handleChangeRateType = (newRateType: RateTypeSelection) => {
    setValue("rateType", newRateType.value)
  }

  const showTotal = !!totalAllocated || !!totalMinutes

  return (
    <div className={commonStyles.budgetEditorWrapper}>
      <Row heading>
        <Column>Role</Column>
        {!hideFinancials && !showNonBillableView && (
          <Column className={commonStyles.alignRight}>Project Rate</Column>
        )}
        <Column className={commonStyles.alignRight}>
          <DropdownSelector
            selectedValue={rateType.value}
            options={RATE_TYPES}
            onChange={handleChangeRateType}
            dataTestId="project-rate-types"
          />
        </Column>
        {!hideFinancials && !showNonBillableView && (
          <Column className={commonStyles.alignRight}>
            {capitalize(formData.budgetMethod)} Budget
          </Column>
        )}
      </Row>
      <div className={commonStyles.tableBody}>
        {extractedRoles.people.map((role) => {
          const rateCardRate = getDefaultRate(rateCard, role.id)
          const canDelete = !role.isScheduled
          return (
            <BudgetItemRow
              key={role.id}
              budgetedRole={role}
              rateCardRate={rateCardRate}
              defaultFullTimeMinutes={defaultFullTimeMinutes}
              showDayView={showDayView}
              canDelete={canDelete}
              deleteTooltip={
                canDelete
                  ? `Remove budget allocation for "${role.name}"`
                  : `Roles that are currently scheduled cannot be removed from the budget allocation.`
              }
            />
          )
        })}
        {!showNonBillableView && !hideFinancials && expenseRole && (
          <OtherExpensesRow key={expenseRole.id} />
        )}
      </div>
      {unallocatedRoles.length > 0 && (
        <div className={commonStyles.tableWidget}>
          <RoleAdder
            dataTest="add-budget-roles"
            roles={unallocatedRoles}
            onAddRole={onAddRole}
            onShowAllRoles={onShowAllRoles}
          />
        </div>
      )}
      {showTotal && (
        <TableInfo
          sticky
          className={classcat([
            commonStyles.totalsWrapper,
            styles.totalsWrapper,
            {
              [styles.nbView]: showNonBillableView,
            },
          ])}
          offset={0}
          dataTest="budget-allocation"
        >
          <div className={styles.textAlignLeft}>Total Role Budget</div>
          <div key="time" data-test="time-total">
            {showDayView
              ? formatMinutesToDays(totalMinutes, defaultFullTimeMinutes)
              : formatMinuteToHours(totalMinutes, "")}
          </div>
          {!showNonBillableView && (
            <div key="money" data-test="budget-total">
              {formatCurrency(totalAllocated)}
            </div>
          )}
        </TableInfo>
      )}
    </div>
  )
}

export default BudgetEditor
